/**
 * What each area of the room looks like.
 *
 * The same night office as the Bug Hunt, told three times with different furniture so the areas read
 * apart at a glance: the chalkboard in Bugs, the SDD boards and the neon in Features, a sofa and the
 * coffee machine in Others. Everything here is decoration except the SDD boards, which show real
 * specifications and open them.
 */

import { memo } from 'react'
import { Chalkboard, CityWindow, NeonSign, Plant, RoutineBoard, type SceneLabels } from '../bughunt/Scene'
import { horizonFor, type FloorSize } from '../bughunt/layout'
import { HolidayDecor } from '../bughunt/Holiday'
import { useScenery } from '../bughunt/scenery'
import { AREA_WIDTH, AREAS, ROOM_WIDTH, areaX, type AreaId } from './areas'
import type { SpecSummary } from '../lib/types'
import { specProgress, type SpecProgressKind } from '../lib/specProgress'
import { useI18n } from '../lib/i18n'

const INK = '#1a1f2c'

/** How many specification boards fit on the Features wall. */
export const BOARD_SLOTS = 4

export const AreaBackdrop = memo(function AreaBackdrop({
  area,
  size,
  labels,
  title,
  people,
  boards,
  onOpenSpec,
}: {
  area: AreaId
  size: FloorSize
  labels: SceneLabels
  title: string
  people: string
  /** Specifications for the Features wall, most active first. Ignored elsewhere. */
  boards: SpecSummary[]
  onOpenSpec: (id: string) => void
}) {
  const x = areaX(area)
  const horizon = horizonFor(size)
  const { height } = size
  const windowHeight = horizon - 92
  const { image, holiday } = useScenery()

  return (
    <g>
      <rect x={x} y="0" width={AREA_WIDTH} height={horizon} fill="url(#bh-wall)" />
      {image ? (
        // One picture across the whole room: each area shows its third of it.
        <svg x={x} y="0" width={AREA_WIDTH} height={horizon} viewBox={`${x} 0 ${AREA_WIDTH} ${horizon}`} overflow="hidden">
          <image href={image} x="0" y="0" width={ROOM_WIDTH} height={horizon} preserveAspectRatio="xMidYMid slice" />
          <rect className="bh-backdrop-dim" x={x} y="0" width={AREA_WIDTH} height={horizon} />
        </svg>
      ) : (
        <g stroke="rgba(255,255,255,0.035)" strokeWidth="2">
          {[180, 360, 640, 820].map((offset) => (
            <line key={offset} x1={x + offset} y1="0" x2={x + offset} y2={horizon} />
          ))}
        </g>
      )}

      {area === 'bugs' && (
        <>
          <Chalkboard x={x + 70} y={44} width={118} height={Math.min(140, horizon - 100)} lines={labels.motto} />
          {!image && <CityWindow x={x + 250} y={34} width={120} height={windowHeight} seed={3} />}
          {!image && <CityWindow x={x + 560} y={34} width={120} height={windowHeight} seed={7} />}
        </>
      )}

      {area === 'features' && (
        <>
          <NeonSign cx={x + AREA_WIDTH / 2} y={44} lines={labels.neon} />
          {[40, 185, 690, 835].map((offset, i) => {
            const spec = boards[i]
            return spec ? (
              <SpecBoard key={offset} x={x + offset} y={40} spec={spec} onOpen={onOpenSpec} />
            ) : image ? null : (
              <CityWindow key={offset} x={x + offset + 10} y={34} width={105} height={windowHeight} seed={11 + i} />
            )
          })}
        </>
      )}

      {area === 'others' && (
        <>
          {!image && <CityWindow x={x + 70} y={34} width={120} height={windowHeight} seed={19} />}
          {!image && <CityWindow x={x + 330} y={34} width={120} height={windowHeight} seed={23} />}
          <RoutineBoard x={x + 780} y={52} width={142} title={labels.routineTitle} items={labels.routine} />
        </>
      )}

      <rect x={x} y={horizon - 10} width={AREA_WIDTH} height="10" fill="var(--bh-skirting)" />
      <rect x={x} y={horizon} width={AREA_WIDTH} height={height - horizon} fill="url(#bh-floor)" />

      <g stroke="var(--bh-line)" strokeWidth="1" opacity="0.45">
        {[0.12, 0.3, 0.55, 0.86].map((t) => (
          <line key={t} x1={x} y1={horizon + (height - horizon) * t} x2={x + AREA_WIDTH} y2={horizon + (height - horizon) * t} />
        ))}
        {Array.from({ length: 11 }, (_, i) => {
          const lx = (AREA_WIDTH / 10) * i
          return <line key={i} x1={x + lx} y1={height} x2={x + AREA_WIDTH / 2 + (lx - AREA_WIDTH / 2) * 0.42} y2={horizon} />
        })}
      </g>

      <ellipse cx={x + AREA_WIDTH * 0.45} cy={horizon + (height - horizon) * 0.56} rx="300" ry="66" fill="var(--bh-rug)" stroke="var(--bh-rug-edge)" strokeWidth="3" />
      {[0.25, 0.75].map((t) => (
        <ellipse key={t} cx={x + AREA_WIDTH * t} cy={horizon + 110} rx="190" ry="95" fill="url(#bh-pool)" />
      ))}

      {area === 'bugs' && <Plant x={x + 225} y={horizon + 12} scale={1} />}
      {area === 'features' && <Plant x={x + 345} y={horizon + 12} scale={0.95} />}
      {area === 'others' && (
        <>
          <CoffeeMachine x={x + 560} y={horizon + 30} />
          <Sofa x={x + 690} y={horizon + 58} />
          <Plant x={x + 250} y={horizon + 12} scale={1.05} />
        </>
      )}

      <HolidayDecor
        holiday={holiday}
        x={x}
        width={AREA_WIDTH}
        spots={
          area === 'bugs' ? [{ x: x + 150, y: horizon + 18 }, { x: x + 470, y: horizon + 16 }]
            : area === 'features' ? [{ x: x + 250, y: horizon + 18 }, { x: x + 760, y: horizon + 16 }]
              : [{ x: x + 120, y: horizon + 18 }, { x: x + 930, y: horizon + 16 }]
        }
      />

      <AreaSign x={x + AREA_WIDTH / 2} title={title} people={people} />
    </g>
  )
})

/** The name of an area and how many people are in it, on a strip at the top of its wall. */
function AreaSign({ x, title, people }: { x: number; title: string; people: string }) {
  const width = Math.max(180, (title.length + people.length) * 7.4 + 40)

  return (
    <g className="room-sign">
      <rect x={x - width / 2} y="4" width={width} height="24" rx="12" />
      <text x={x} y="20" textAnchor="middle">
        <tspan className="room-sign__title">{title.toUpperCase()}</tspan>
        <tspan className="room-sign__people" dx="8">{people}</tspan>
      </text>
    </g>
  )
}

/** Splits a title into at most two lines of about `width` characters, for a board. */
function wrap(text: string, width: number): string[] {
  const words = text.split(/\s+/)
  const lines: string[] = ['']
  for (const word of words) {
    const current = lines[lines.length - 1]!
    if ((current + ' ' + word).trim().length <= width) lines[lines.length - 1] = (current + ' ' + word).trim()
    else if (lines.length < 2) lines.push(word)
    else {
      lines[1] = `${lines[1]!.slice(0, width - 1)}…`
      break
    }
  }
  return lines
}

/**
 * A specification pinned to the wall as a blueprint: kind, key, title, how far along, and how many
 * people are on it now. The figure is the agents' estimate when there is one, else tasks done, and
 * says which.
 */
/** A colour per kind, so the three are not mistaken for each other across a room. */
const BAR: Record<SpecProgressKind, string> = {
  delivered: '#5CE1A6',
  tasks: '#5CB8FF',
  document: '#C4B5FD',
}

function SpecBoard({ x, y, spec, onOpen }: { x: number; y: number; spec: SpecSummary; onOpen: (id: string) => void }) {
  const { t } = useI18n()
  const width = 125
  const height = 116
  const working = spec.liveSessions.filter((s) => s.activityState !== 'Idle').length

  // Which of the three figures this specification has, and which one it is — the board says so,
  // because "100%" meaning "the document is written" and "100%" meaning "it is built" cannot look
  // the same. See lib/specProgress.ts.
  const progress = specProgress(spec)

  return (
    <g
      className="room-board"
      transform={`translate(${x} ${y})`}
      onClick={(event) => {
        event.stopPropagation()
        onOpen(spec.id)
      }}
      role="button"
      tabIndex={0}
      aria-label={`${spec.key} ${spec.title}`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(spec.id)
        }
      }}
    >
      <rect x="-3" y="-3" width={width + 6} height={height + 6} rx="5" fill="#5B677A" stroke={INK} strokeWidth="1.4" />
      <rect width={width} height={height} rx="3" className="room-board__paper" />
      <g stroke="rgba(255,255,255,0.12)" strokeWidth="0.8">
        {Array.from({ length: 9 }, (_, i) => <line key={`v${i}`} x1={i * 14 + 6} y1="0" x2={i * 14 + 6} y2={height} />)}
        {Array.from({ length: 8 }, (_, i) => <line key={`h${i}`} x1="0" y1={i * 14 + 6} x2={width} y2={i * 14 + 6} />)}
      </g>

      <rect x="8" y="8" width="30" height="14" rx="3" fill={spec.kind === 'Prd' ? '#8B5CF6' : '#0EA5E9'} />
      <text x="23" y="18.5" textAnchor="middle" className="room-board__kind">{spec.kind === 'Prd' ? 'PRD' : 'SDD'}</text>
      <text x="8" y="37" className="room-board__key">{spec.key.length > 17 ? `${spec.key.slice(0, 16)}…` : spec.key}</text>
      {wrap(spec.title, 19).map((line, i) => (
        <text key={i} x="8" y={52 + i * 12} className="room-board__title">{line}</text>
      ))}

      {progress && (
        <>
          <text x="8" y="81" className="room-board__measure">{t(`specs.measure.${progress.kind}`)}</text>
          <rect x="8" y="86" width={width - 16} height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
          <rect x="8" y="86" width={((width - 16) * progress.percent) / 100} height="5" rx="2.5" fill={BAR[progress.kind]} />
          <text x={width - 8} y="81" textAnchor="end" className="room-board__pct">
            {progress.isEstimate ? '~' : ''}{Math.round(progress.percent)}%
          </text>
        </>
      )}

      {working > 0 && (
        <g transform="translate(8 104)">
          <circle cx="4" cy="-3" r="3" fill="#5CE1A6" />
          <text x="11" y="0" className="room-board__people">{working}</text>
        </g>
      )}
      {spec.openBugCount > 0 && (
        <g transform={`translate(${width - 30} 104)`}>
          <circle cx="4" cy="-3" r="3" fill="#E5484D" />
          <text x="11" y="0" className="room-board__people">{spec.openBugCount}</text>
        </g>
      )}
    </g>
  )
}

function CoffeeMachine({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="26" ry="5" fill="rgba(0,0,0,0.3)" />
      <rect x="-22" y="-70" width="44" height="72" rx="6" fill="#3A4358" stroke={INK} strokeWidth="1.5" />
      <rect x="-16" y="-62" width="32" height="18" rx="3" fill="#10182A" stroke={INK} strokeWidth="1" />
      <circle cx="10" cy="-53" r="2.4" fill="#5CE1A6" />
      <rect x="-12" y="-38" width="24" height="20" rx="2" fill="#1B2233" />
      <rect x="-6" y="-26" width="12" height="9" rx="2" fill="#E9EEF6" stroke={INK} strokeWidth="1" />
      <path d="M -3 -30 q -2 -5 1 -9 M 3 -30 q -2 -5 1 -9" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" strokeLinecap="round" />
    </g>
  )
}

function Sofa({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="4" rx="90" ry="9" fill="rgba(0,0,0,0.3)" />
      <rect x="-80" y="-58" width="160" height="40" rx="12" fill="#4B5A7D" stroke={INK} strokeWidth="1.6" />
      <rect x="-86" y="-30" width="172" height="30" rx="8" fill="#56678D" stroke={INK} strokeWidth="1.6" />
      <rect x="-96" y="-46" width="22" height="46" rx="9" fill="#4B5A7D" stroke={INK} strokeWidth="1.6" />
      <rect x="74" y="-46" width="22" height="46" rx="9" fill="#4B5A7D" stroke={INK} strokeWidth="1.6" />
      <path d="M 0 -30 v 30" stroke="rgba(0,0,0,0.25)" strokeWidth="1.4" />
      <rect x="-60" y="-52" width="34" height="22" rx="7" fill="#F5B83D" stroke={INK} strokeWidth="1.2" transform="rotate(-8 -43 -41)" />
    </g>
  )
}

/** The walls between areas: a pillar on the wall and a line across the floor. */
export function AreaDividers({ size }: { size: FloorSize }) {
  const horizon = horizonFor(size)

  return (
    <g aria-hidden="true">
      {AREAS.slice(1).map((area) => {
        const x = areaX(area)
        return (
          <g key={area}>
            <rect x={x - 9} y="0" width="18" height={horizon} fill="#0A101C" stroke={INK} strokeWidth="1.2" />
            <line x1={x} y1={horizon} x2={x} y2={size.height} stroke="rgba(255,255,255,0.08)" strokeWidth="2" strokeDasharray="10 8" />
          </g>
        )
      })}
    </g>
  )
}
